import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

import { PageHeader } from "@/client/components/page-header";
import { Button } from "@/client/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/client/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/client/components/ui/form";
import { Input } from "@/client/components/ui/input";
import { useAuthStore } from "@/client/features/auth/auth-store";
import { meApi } from "@/client/features/account/me-api";
import { ApiError } from "@/client/lib/api";
import { authApi } from "@/client/features/auth/auth-api";
import { MfaChallenge } from "@/client/pages/auth/mfa-challenge";
import { PASSWORD_POLICY_HINT, passwordSchema as passwordPolicy } from "@/shared/password";
import type { SessionUser } from "@/shared/types";

function roleLabel(user: SessionUser): string {
  if (user.kind === "staff") {
    return user.staffRole === "admin" ? "Staff admin" : "Staff";
  }
  return user.tenantRole === "tenant_admin" ? "Admin" : "Member";
}

function ProfileField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-sm">{value}</span>
    </div>
  );
}

function ProfileCard({ user }: { user: SessionUser }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Profile</CardTitle>
        <CardDescription>Your account details.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <ProfileField label="Name" value={user.name ?? "—"} />
        <ProfileField label="Email" value={user.email} />
        <ProfileField label="Role" value={roleLabel(user)} />
      </CardContent>
    </Card>
  );
}

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, "Enter your current password"),
    newPassword: passwordPolicy,
    confirmPassword: z.string().min(1, "Confirm your new password"),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    path: ["confirmPassword"],
    message: "Passwords don't match",
  })
  .refine((v) => v.newPassword !== v.currentPassword, {
    path: ["newPassword"],
    message: "Choose a password different from your current one",
  });

type PasswordValues = z.infer<typeof passwordSchema>;

function ChangePasswordCard() {
  const [pendingValues, setPendingValues] = useState<PasswordValues | null>(null);
  const [totp, setTotp] = useState("");
  const [steppingUp, setSteppingUp] = useState(false);
  // Once step-up succeeds the session is already MFA-verified, so a retry after
  // a later failure must not resend the (now consumed) TOTP.
  const [steppedUp, setSteppedUp] = useState(false);
  const form = useForm<PasswordValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { currentPassword: "", newPassword: "", confirmPassword: "" },
  });

  const change = async (values: PasswordValues) => {
    await meApi.changePassword({
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
    });
    toast.success("Password updated. Other devices have been signed out.");
    form.reset({ currentPassword: "", newPassword: "", confirmPassword: "" });
  };

  const onSubmit = async (values: PasswordValues) => {
    try {
      await change(values);
    } catch (err) {
      if (err instanceof ApiError && err.code === "mfa_step_up_required") {
        setSteppedUp(false);
        setPendingValues(values);
        return;
      }
      const message =
        err instanceof ApiError ? err.message : "Unable to update your password. Please try again.";
      toast.error(message);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Change password</CardTitle>
        <CardDescription>Updating your password signs out your other devices.</CardDescription>
      </CardHeader>
      <CardContent>
        <Dialog
          open={pendingValues !== null}
          onOpenChange={(open) => {
            if (!open) {
              setPendingValues(null);
              setTotp("");
              setSteppedUp(false);
            }
          }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Confirm with your authenticator</DialogTitle>
              <DialogDescription>
                Changing your password requires a recent MFA verification.
              </DialogDescription>
            </DialogHeader>
            <Input
              aria-label="Authenticator code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={totp}
              onChange={(event) => setTotp(event.target.value)}
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setPendingValues(null)}>
                Cancel
              </Button>
              <Button
                disabled={steppingUp || (!steppedUp && !/^\d{6}$/.test(totp))}
                onClick={async () => {
                  if (!pendingValues) return;
                  setSteppingUp(true);
                  try {
                    if (!steppedUp) {
                      await authApi.stepUpMfa(totp);
                      setSteppedUp(true);
                    }
                    await change(pendingValues);
                    setPendingValues(null);
                    setTotp("");
                    setSteppedUp(false);
                  } catch (error) {
                    toast.error(
                      error instanceof ApiError ? error.message : "Unable to verify MFA.",
                    );
                  } finally {
                    setSteppingUp(false);
                  }
                }}
              >
                {steppingUp ? "Verifying…" : "Verify and update"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="grid max-w-sm gap-4">
            <FormField
              control={form.control}
              name="currentPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Current password</FormLabel>
                  <FormControl>
                    <Input type="password" autoComplete="current-password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="newPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New password</FormLabel>
                  <FormControl>
                    <Input type="password" autoComplete="new-password" {...field} />
                  </FormControl>
                  <p className="text-muted-foreground text-xs">{PASSWORD_POLICY_HINT}</p>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm new password</FormLabel>
                  <FormControl>
                    <Input type="password" autoComplete="new-password" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-fit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? "Updating…" : "Update password"}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}

function MfaSecurityCard({ onReplace }: { onReplace: () => void }) {
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState<"replace" | "codes">("replace");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [needsStepUp, setNeedsStepUp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [codes, setCodes] = useState<string[] | null>(null);

  const reset = () => {
    setPassword("");
    setTotp("");
    setNeedsStepUp(false);
    setCodes(null);
  };

  const perform = async () => {
    if (action === "replace") {
      await meApi.replaceMfa(password);
      setOpen(false);
      reset();
      onReplace();
      return;
    }
    const response = await meApi.regenerateRecoveryCodes(password);
    setCodes(response.recoveryCodes);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      if (needsStepUp) {
        await authApi.stepUpMfa(totp);
        setNeedsStepUp(false);
      }
      await perform();
    } catch (error) {
      if (error instanceof ApiError && error.code === "mfa_step_up_required") {
        setNeedsStepUp(true);
        toast.message("Enter a current authenticator code to continue.");
      } else {
        toast.error(error instanceof ApiError ? error.message : "Unable to update MFA.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Multi-factor authentication</CardTitle>
        <CardDescription>
          Your authenticator is required at sign-in. Recovery codes work once if you lose access.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={() => {
            setAction("replace");
            setOpen(true);
          }}
        >
          Replace authenticator
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            setAction("codes");
            setOpen(true);
          }}
        >
          Generate new recovery codes
        </Button>
        <Dialog
          open={open}
          onOpenChange={(next) => {
            setOpen(next);
            if (!next) reset();
          }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {action === "replace" ? "Replace authenticator?" : "Generate new recovery codes?"}
              </DialogTitle>
              <DialogDescription>
                {action === "replace"
                  ? "This revokes other portal sessions after the replacement is confirmed."
                  : "All existing recovery codes will stop working."}
              </DialogDescription>
            </DialogHeader>
            {codes ? (
              <div className="grid gap-2">
                <div className="bg-muted grid gap-1 rounded-md p-4 font-mono text-sm">
                  {codes.map((code) => (
                    <span key={code}>{code}</span>
                  ))}
                </div>
                <p className="text-muted-foreground text-xs">
                  Save these now. They will not be shown again.
                </p>
              </div>
            ) : (
              <div className="grid gap-3">
                <div className="grid gap-2">
                  <label htmlFor="mfa-confirm-password" className="text-sm font-medium">
                    Current password
                  </label>
                  <Input
                    id="mfa-confirm-password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                </div>
                {needsStepUp ? (
                  <div className="grid gap-2">
                    <label htmlFor="mfa-step-up-code" className="text-sm font-medium">
                      Authenticator code
                    </label>
                    <Input
                      id="mfa-step-up-code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={totp}
                      onChange={(event) => setTotp(event.target.value)}
                    />
                  </div>
                ) : null}
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                {codes ? "Done" : "Cancel"}
              </Button>
              {!codes ? (
                <Button
                  type="button"
                  onClick={() => void submit()}
                  disabled={
                    submitting || password.length === 0 || (needsStepUp && !/^\d{6}$/.test(totp))
                  }
                >
                  {submitting ? "Confirming…" : "Continue"}
                </Button>
              ) : null}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

function DeleteAccountCard() {
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [needsStepUp, setNeedsStepUp] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setPassword("");
    setTotp("");
    setNeedsStepUp(false);
  };

  const performDelete = async () => {
    await meApi.deleteAccount(password);
    setOpen(false);
    reset();
    // The server already revoked sessions and cleared the cookie; drop local
    // auth state and send the user to the sign-in screen.
    await logout();
    toast.success("Your account has been deleted.");
    navigate("/login", { replace: true });
  };

  const onConfirm = async () => {
    setSubmitting(true);
    try {
      if (needsStepUp) {
        await authApi.stepUpMfa(totp);
        setNeedsStepUp(false);
      }
      await performDelete();
    } catch (err) {
      if (err instanceof ApiError && err.code === "mfa_step_up_required") {
        setNeedsStepUp(true);
        toast.message("Enter a current authenticator code to continue.");
      } else {
        toast.error(
          err instanceof ApiError
            ? err.message
            : "Unable to delete your account. Please try again.",
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <CardTitle className="text-destructive">Delete account</CardTitle>
        <CardDescription>
          Permanently remove your access to the portal. This can't be undone.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Dialog
          open={open}
          onOpenChange={(next) => {
            setOpen(next);
            if (!next) reset();
          }}
        >
          <DialogTrigger asChild>
            <Button variant="destructive" className="w-fit">
              Delete my account
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Delete your account?</DialogTitle>
              <DialogDescription>
                This signs you out everywhere and removes your access. Enter your password to
                confirm.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-3">
              <div className="grid gap-2">
                <label htmlFor="delete-confirm-password" className="text-sm font-medium">
                  Password
                </label>
                <Input
                  id="delete-confirm-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              {needsStepUp ? (
                <div className="grid gap-2">
                  <label htmlFor="delete-step-up-code" className="text-sm font-medium">
                    Authenticator code
                  </label>
                  <Input
                    id="delete-step-up-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={totp}
                    onChange={(event) => setTotp(event.target.value)}
                  />
                </div>
              ) : null}
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline" disabled={submitting}>
                  Cancel
                </Button>
              </DialogClose>
              <Button
                type="button"
                variant="destructive"
                onClick={onConfirm}
                disabled={
                  submitting || password.length === 0 || (needsStepUp && !/^\d{6}$/.test(totp))
                }
              >
                {submitting ? "Deleting…" : "Delete account"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

export function SettingsPage() {
  const user = useAuthStore((s) => s.user);
  // Staff "view as" is read-only: MFA, password, and delete all hit the
  // impersonation guard's 403, so hide those write cards during a view-as
  // session (matching api-tokens/storage/allowed-ips).
  const impersonating = useAuthStore((s) => s.impersonation != null);
  const [replacingMfa, setReplacingMfa] = useState(false);
  if (!user) {
    return null;
  }
  if (replacingMfa) {
    return (
      <div className="mx-auto w-full max-w-lg">
        <MfaChallenge
          enrolling
          onComplete={() => {
            setReplacingMfa(false);
            toast.success("Authenticator replaced.");
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Settings" description="Manage your account and preferences." />
      <ProfileCard user={user} />
      {/* Staff authenticate via SSO and have no portal password; staff view-as
          is read-only, so these self-service write actions are hidden there.
          The MFA card manages an existing authenticator, so it only applies to
          customers who are actually enrolled (replace/regenerate 500 otherwise). */}
      {user.kind === "customer" && !impersonating && user.mfaEnabled ? (
        <MfaSecurityCard onReplace={() => setReplacingMfa(true)} />
      ) : null}
      {user.kind === "customer" && !impersonating ? <ChangePasswordCard /> : null}
      {user.kind === "customer" && !impersonating ? <DeleteAccountCard /> : null}
    </div>
  );
}
